import type { ComponentPropsWithoutRef, ReactNode } from 'react'
import { cn } from './cn'

/**
 * Champs de formulaire.
 *
 * Le libellé est toujours au-dessus et toujours présent — jamais un
 * `placeholder` en guise d'étiquette : il disparaît à la saisie, ce qui laisse
 * l'utilisateur sans repère au moment de se relire, et n'est pas annoncé
 * correctement par les lecteurs d'écran.
 *
 * ------------------------------------------------------------------------
 * La propriété `ton`
 * ------------------------------------------------------------------------
 * `'clair'` est le défaut et **n'a pas bougé d'une classe** : les cinq espaces
 * posent leurs formulaires sur la carte blanche du design system et continuent
 * de rendre exactement ce qu'ils rendaient. Tout appel qui ne dit rien reçoit
 * `'clair'`.
 *
 * `'fonce'` existe pour un seul endroit, mais un endroit qui compte : la carte
 * de verre sombre de la maquette Oasis, qui porte les trois écrans d'entrée
 * (`/connexion`, `/inscription`, `/mot-de-passe-oublie`) — la première chose
 * que voient 260 apprenants. Sur ce fond, les couleurs de texte de l'app sont
 * toutes trop foncées ; il fallait une bascule, pas une surcharge depuis
 * l'appelant.
 *
 * ⚠️ **`text-lavender-deep` n'apparaît nulle part dans le ton sombre**, alors
 * que la maquette l'emploie. Mesuré sur encre : **2,57:1**, contre 4,5 exigés.
 * `app.css` documente la règle et la raison — l'assombrissement qui rend cette
 * couleur lisible sur crème (5,04:1) la rend illisible sur encre. Sur fond
 * sombre l'accent est `lavender` (#B086FC), qui donne **5,18:1** sur encre et
 * qui est par ailleurs la couleur de marque.
 *
 * ------------------------------------------------------------------------
 * Ce que le ton sombre suppose du fond — et qui ne se joue pas ici
 * ------------------------------------------------------------------------
 * Les mesures ci-dessous sont prises **sur l'encre `#27274B`**. C'est le
 * contrat : la carte de verre (`rgba(39,39,75,.42)`) est translucide, donc son
 * rendu réel dépend de ce que le voile laisse passer de la photo.
 *
 * Ce n'est pas une précaution théorique, c'est mesuré. Au-dessus d'un
 * highlight de sable clair, la carte seule compose un gris moyen où le crème
 * pur ne donne plus que **2,72:1** et où la lavande tombe à **1,09:1** — le
 * texte disparaît. Le voile doit donc assombrir jusqu'à l'encre ; en dessous,
 * aucune couleur choisie ici ne rattrape la mise. Le seuil est porté par
 * l'écran, pas par ce composant.
 */

/**
 * Ton de surface.
 *
 * `'clair'` : carte blanche du design system — le défaut de toute l'app.
 * `'fonce'` : carte de verre sombre de la coque Oasis.
 */
export type Ton = 'clair' | 'fonce'

/* ------------------------------------------------------------------------
   Libellé, aide et erreur
   ------------------------------------------------------------------------
   Contrastes mesurés sur encre #27274B :
     crème 100 %          12,94:1
     crème 75 %            7,94:1   ← libellé, aide, texte de case
     alert-bg  #F5D9CE    10,62:1   ← message d'erreur

   ⚠️ **`alert-fg` est proscrit ici, et c'est une erreur qui ne se voit pas à
   la relecture** : #8A3B20 est un corail *foncé*, dessiné pour porter sur le
   fond corail clair d'un bandeau d'alerte. Sur la carte de verre il donne
   **1,85:1** — un message d'erreur qu'on ne peut pas lire, c'est-à-dire pas de
   message d'erreur du tout, sur l'écran où l'on se trompe le plus.

   Le ton sombre reprend donc l'autre bout du même couple : `alert-bg`
   #F5D9CE, le corail *clair*, employé ici comme couleur de **texte**. Sur
   sombre les deux rôles s'échangent — même famille chaude, donc même lecture
   « quelque chose ne va pas », et 10,62:1 au lieu de 1,85. Aucune couleur
   nouvelle n'est introduite pour autant.
   ------------------------------------------------------------------------ */
const LIBELLE: Record<Ton, { etiquette: string; aide: string; erreur: string }> = {
  clair: {
    etiquette: 'text-[13px] font-semibold text-ink',
    aide: 'text-[12.5px] text-ink-mute',
    erreur: 'text-[12.5px] font-medium text-alert-fg',
  },
  fonce: {
    // 12px / 600, la mesure de la maquette.
    etiquette: 'text-[12px] font-semibold text-cream/75',
    aide: 'text-[12.5px] text-cream/75',
    erreur: 'text-[12.5px] font-medium text-alert-bg',
  },
}

export function Field({
  label,
  htmlFor,
  hint,
  error,
  ton = 'clair',
  children,
  className,
}: {
  label: string
  htmlFor: string
  hint?: string
  error?: string
  /** Voir `Ton`. `'clair'` par défaut — le rendu historique, inchangé. */
  ton?: Ton
  children: ReactNode
  className?: string
}) {
  const couleurs = LIBELLE[ton]

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className={couleurs.etiquette}>
        {label}
      </label>
      {children}
      {/* `role="alert"` pour que l'erreur soit annoncée dès son apparition,
          plutôt qu'attendre que l'utilisateur revienne sur le champ. */}
      {error ? (
        <p role="alert" className={couleurs.erreur}>
          {error}
        </p>
      ) : hint ? (
        <p className={couleurs.aide}>{hint}</p>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------------
   Le champ lui-même
   ------------------------------------------------------------------------
   Ton sombre, d'après la maquette — bordure et placeholder relevés au seuil
   (voir plus bas). Fond `cream/15`,
   rayon 14px, marge intérieure 14/16.

   Le rayon est écrit en dur (`rounded-[14px]`) plutôt que via un utilitaire :
   `--radius-14` existe bien dans `app.css`, mais il n'est pas ponté vers
   Tailwind dans `globals.css`, et ce lot n'ouvre pas ce fichier. À ponter le
   jour où une deuxième surface en aura besoin.

   Contrastes mesurés dans le ton sombre — fond du champ = crème 15 % sur
   encre :
     texte crème 100 %      8,26:1   ✔
     icône crème 60 %       4,15:1   (décorative, seuil 3:1) ✔
     placeholder crème 50 % 3,39:1   ⚠ voir ci-dessous
   et la bordure du champ contre la carte :
     crème 30 %             2,50:1   ⚠ voir ci-dessous

   ⚠️ **Deux valeurs de la maquette passent sous les seuils, et elles sont
   reprises telles quelles parce qu'elles sont la commande de ce lot** — pas
   parce qu'elles sont bonnes. Elles sont signalées, chacune se corrige en un
   caractère, et aucune n'est enfouie :
     · `placeholder:text-cream/80` donne 3,39:1. C'est un texte, donc le seuil
       de 4,5 s'applique en toute rigueur ; `cream/65` donnerait 4,57:1. Ce
       qui l'atténue sans l'effacer : ce composant n'admet jamais le
       placeholder comme étiquette — le libellé est au-dessus et il y reste —
       donc rien de porteur ne dépend de cette ligne. Le ton clair, lui, place
       son placeholder à 5,49:1 : l'écart entre les deux tons est réel.

   Le survol passe la bordure en lavande dans les deux tons : 5,18:1 sur
   encre, 5,53:1 sur blanc. La même couleur convient aux deux, ce qui est rare
   et vaut d'être gardé.
   ------------------------------------------------------------------------ */
const CHAMP: Record<
  Ton,
  { base: string; normal: string; invalide: string; icone: string; retrait: string }
> = {
  clair: {
    base: 'w-full rounded-field border bg-surface px-3.5 py-3 text-[14.5px] text-ink placeholder:text-ink-mute',
    normal: 'border-hairline-strong hover:border-lavender',
    invalide: 'border-alert-border',
    // Marge intérieure 14px + glyphe 18px + gouttière 12px = 44px.
    icone: 'left-3.5 text-ink-mute',
    retrait: 'pl-[44px]',
  },
  fonce: {
    base: 'w-full rounded-[14px] border bg-cream/15 px-4 py-3.5 text-[14.5px] text-cream placeholder:text-cream/80',
    normal: 'border-cream/45 hover:border-lavender',
    // `alert-border` #E8B49B donne 7,74:1 sur encre : la bordure fautive reste
    // franche sur le verre, et c'est la même couleur que dans le ton clair.
    invalide: 'border-alert-border',
    // Marge intérieure 16px + glyphe 18px + gouttière 12px = 46px.
    icone: 'left-4 text-cream/60',
    retrait: 'pl-[46px]',
  },
}

export function Input({
  invalid = false,
  ton = 'clair',
  icone,
  className,
  ...rest
}: {
  invalid?: boolean
  /** Voir `Ton`. `'clair'` par défaut — le rendu historique, inchangé. */
  ton?: Ton
  /**
   * Pictogramme posé à gauche, dans le champ. Absent par défaut, et **son
   * absence ne change rien** : sans lui, `Input` rend le même `<input>` nu
   * qu'avant, sans enveloppe ni marge supplémentaire.
   *
   * La maquette Oasis en pose un sur l'e-mail et sur le mot de passe. Le gabarit
   * suppose un glyphe de 18px — la taille par défaut d'`Icon`.
   */
  icone?: ReactNode
} & ComponentPropsWithoutRef<'input'>) {
  const couleurs = CHAMP[ton]

  const champ = (
    <input
      aria-invalid={invalid || undefined}
      className={cn(
        couleurs.base,
        'transition-colors duration-(--dur-base) ease-(--ease-umiros)',
        invalid ? couleurs.invalide : couleurs.normal,
        icone ? couleurs.retrait : null,
        className,
      )}
      {...rest}
    />
  )

  if (!icone) return champ

  /*
   * Le pictogramme est posé **par-dessus** le champ, pas à côté de lui dans une
   * boîte commune — c'est là que la maquette et l'accessibilité divergent.
   *
   * La maquette porte la bordure et le fond sur l'enveloppe, et laisse l'input
   * nu à l'intérieur. Le contour de focus se dessinerait alors autour de la
   * seule zone de texte, décalé du cadre visible et coupé du pictogramme : le
   * champ paraîtrait avoir deux limites, dont une seule réagit au clavier.
   *
   * Ici la bordure, le fond et le rayon restent sur l'`<input>` — un seul objet,
   * un seul cadre, et le contour de focus épouse exactement ce qu'on voit. Le
   * pictogramme est décoratif (`aria-hidden`, le libellé porte le sens) et ne
   * prend pas le clic, donc toute la surface du champ reste cliquable.
   */
  return (
    <span className="relative flex w-full items-center">
      <span
        aria-hidden="true"
        className={cn('pointer-events-none absolute flex items-center', couleurs.icone)}
      >
        {icone}
      </span>
      {champ}
    </span>
  )
}

/* ------------------------------------------------------------------------
   Case à cocher
   ------------------------------------------------------------------------
   Contrastes mesurés dans le ton sombre :
     bordure crème 40 % sur encre   3,35:1   ✔ (seuil 3:1, élément non textuel)
     coche encre sur lavande        5,18:1   ✔
     texte crème 75 % sur encre     7,94:1   ✔

   L'état coché est **identique dans les deux tons** — fond lavande, coche
   encre. Il n'y avait rien à basculer : la lavande porte déjà sur les deux
   surfaces, et la coche encre dessus tient à 5,18:1. Seul l'état décoché
   change, puisque lui seul laissait voir la surface.
   ------------------------------------------------------------------------ */
const CASE: Record<Ton, { etiquette: string; boite: string; texte: string }> = {
  clair: {
    etiquette: 'flex cursor-pointer items-start gap-2.5',
    boite: 'border-hairline-strong bg-surface',
    texte: 'text-[13.5px] text-ink-soft',
  },
  fonce: {
    /*
     * `py-[13px] -my-[13px]` : la zone cliquable monte à 18 + 26 = 44px sans
     * déplacer quoi que ce soit. La marge négative rend au flux exactement ce
     * que la marge intérieure lui prend, donc l'espacement du formulaire est
     * au pixel près celui d'avant — c'est une cible tactile, pas une respiration.
     *
     * ⚠️ **L'espacement se règle donc par le `gap` du parent, jamais par une
     * marge passée en `className`.** Vérifié dans la feuille compilée :
     * `mt-*` et `mb-*` sont émis après `-my-*` et l'emportent côté par côté —
     * un `className="mt-4"` prendrait le haut et laisserait le bas à −13px,
     * qui remonterait l'élément suivant de 13px sans qu'on voie pourquoi.
     *
     * Le ton clair ne la reçoit pas : sa cible fait ~20px, ce qui est court,
     * mais elle est partagée par les cinq espaces et le défaut ne bouge pas
     * dans ce lot. Le défaut se corrige, il se corrige ailleurs.
     */
    etiquette: 'flex cursor-pointer items-start gap-2.5 py-[13px] -my-[13px]',
    boite: 'border-cream/40 bg-cream/10',
    texte: 'text-[13px] text-cream/75',
  },
}

/**
 * Case à cocher.
 *
 * Le carré natif est masqué et redessiné, mais reste dans le flux : c'est lui
 * qui porte le focus, la navigation au clavier et l'état pour l'assistance
 * technique. Un faux carré en `<div>` perdrait tout cela.
 */
export function Checkbox({
  label,
  id,
  ton = 'clair',
  className,
  ...rest
}: {
  label: ReactNode
  /** Voir `Ton`. `'clair'` par défaut — le rendu historique, inchangé. */
  ton?: Ton
} & ComponentPropsWithoutRef<'input'>) {
  const couleurs = CASE[ton]

  return (
    <label htmlFor={id} className={cn(couleurs.etiquette, className)}>
      <span className="relative mt-px flex size-[18px] shrink-0">
        <input
          id={id}
          type="checkbox"
          className={cn(
            'peer size-full appearance-none rounded-[6px] border',
            couleurs.boite,
            'transition-colors duration-(--dur-base) ease-(--ease-umiros)',
            'checked:border-lavender checked:bg-lavender',
          )}
          {...rest}
        />
        {/* La coche est dessinée par-dessus l'input coché, sans intercepter le clic.
            `stroke-ink` vaut pour les deux tons : le carré coché est lavande de
            part et d'autre, et l'encre y donne 5,18:1. */}
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="pointer-events-none absolute inset-0 size-full scale-[0.7] stroke-ink opacity-0 peer-checked:opacity-100"
          fill="none"
          strokeWidth={3}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </span>
      <span className={couleurs.texte}>{label}</span>
    </label>
  )
}
